import {useEffect} from 'react';

import {createSignal} from '@pexip/signal';

export const keydownSignal = createSignal<KeyboardEvent>({
    allowEmittingWithoutObserver: true,
    name: 'useHotKey:keydownEvent',
});

export const excludedElements = ['INPUT', 'TEXTAREA'];

const emitKeydownSignal = (event: KeyboardEvent) => keydownSignal.emit(event);
let activeHotKeyCount = 0;

export interface DisallowedKeys {
    metaKey?: boolean;
    ctrlKey?: boolean;
}

type ModifierKeys = Pick<
    KeyboardEvent,
    'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'
>;

/**
 * Listens for a single-character shortcut outside text inputs.
 * For example, `{key: 'r', requiredKeys: {shiftKey: true, metaKey: true}}`
 * matches Shift+Meta+R.
 */
export const useHotKey = ({
    key,
    handler,
    requiredKeys,
    disallowedKeys = {
        metaKey: !requiredKeys?.metaKey,
        ctrlKey: !requiredKeys?.ctrlKey,
    },
    isDisabled = false,
    preventDefault = false,
}: {
    key: string;
    handler: () => void;
    isDisabled?: boolean;
    disallowedKeys?: DisallowedKeys;
    /**
     * Modifier states to match. Required Shift ignores letter case.
     * Required Meta/Control are allowed unless explicitly disallowed.
     */
    requiredKeys?: Partial<ModifierKeys>;
    /** Prevent the browser action only when the shortcut matches. */
    preventDefault?: boolean;
}) => {
    if (key.length !== 1) {
        throw new Error('Hotkey length is limited to 1 currently');
    }

    useEffect(() => {
        if (isDisabled) {
            return;
        }

        if (activeHotKeyCount === 0) {
            document.addEventListener('keydown', emitKeydownSignal);
        }
        activeHotKeyCount += 1;

        return () => {
            activeHotKeyCount -= 1;
            if (activeHotKeyCount === 0) {
                document.removeEventListener('keydown', emitKeydownSignal);
            }
        };
    }, [isDisabled]);

    useEffect(
        () =>
            keydownSignal.add(event => {
                if (isDisabled) {
                    return;
                }

                if (
                    excludedElements.includes(
                        document.activeElement?.tagName || '',
                    )
                ) {
                    return;
                }

                let disallowedKey: keyof DisallowedKeys;
                for (disallowedKey in disallowedKeys) {
                    if (disallowedKeys[disallowedKey] && event[disallowedKey]) {
                        return;
                    }
                }

                if (requiredKeys) {
                    let requiredKey: keyof ModifierKeys;
                    for (requiredKey in requiredKeys) {
                        const requiredState = requiredKeys[requiredKey];
                        if (
                            requiredState !== undefined &&
                            event[requiredKey] !== requiredState
                        ) {
                            return;
                        }
                    }
                }

                let pressedKey = event.key;
                if (requiredKeys?.shiftKey) {
                    pressedKey = pressedKey.toLowerCase();
                } else if (event.getModifierState?.('CapsLock')) {
                    pressedKey = event.shiftKey
                        ? event.key.toUpperCase()
                        : event.key.toLowerCase();
                }

                const expectedKey = requiredKeys?.shiftKey
                    ? key.toLowerCase()
                    : key;
                if (expectedKey !== pressedKey) {
                    return;
                }

                if (preventDefault) {
                    event.preventDefault();
                }
                if (event.repeat) {
                    return;
                }
                return handler();
            }),
        [
            key,
            handler,
            disallowedKeys,
            requiredKeys,
            isDisabled,
            preventDefault,
        ],
    );
};
